import { ChangeEvent, FC, MouseEvent, useEffect, useMemo, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableFooter,
  TableHead,
  TableRow,
} from "@mui/material";
import { v4 } from "uuid";

import { TrainDefectSortColumn } from "@framework/types";
import { SortDirection } from "@framework/types-collection";

import { ReportDownloadButton } from "../../../../../../components/common/buttons/report-download";
import { SemaforIcon, formatDateHumanReadable } from "../../../../../../utils";
import { StyledTablePagination } from "../../ui/table-pagination";
import { useScreenSize } from "../../../../../../utils/screen";
import { SortButton } from "../../../../../../components/common/buttons/sort-button";
import { IDefectResponse } from "./interfaces";
import { StyledHeaderTableCell, StyledTableCell } from "./styled";

export interface IDefectsTable {
  defects: IDefectResponse[];
  handleSort: (value: TrainDefectSortColumn) => () => void;
  sortColumn: TrainDefectSortColumn;
  sortOrder: SortDirection;
}

export const DefectsTable: FC<IDefectsTable> = props => {
  const { defects, handleSort, sortColumn, sortOrder } = props;

  const { isExtraLargeScreen } = useScreenSize();
  const { formatMessage } = useIntl();
  const defectsId = "pages.scheme.defects";
  const headersId = `${defectsId}.headers`;

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);

  const rows = useMemo(
    () =>
      defects.map(defect => {
        const {
          Position,
          CreationType,
          DefectName,
          DefectStatus,
          Rate,
          Date,
          idReport,
          PointName,
          ComponentName,
          MechanismName,
        } = defect as any;

        const resolvedPosition = Position ?? PointName ?? ComponentName ?? MechanismName ?? "";

        return {
          position: resolvedPosition,
          date: formatDateHumanReadable(Date),
          name: DefectName,
          rate: Rate,
          source: formatMessage({ id: `enums.defectSource.${CreationType}` }),
          idReport,
          status: formatMessage({ id: `enums.defectStatus.${DefectStatus}` }),
        };
      }),
    [defects, formatMessage],
  );

  // Avoid a layout jump when reaching the last page with empty rows.
  const emptyRows = page > 0 ? Math.max(0, (1 + page) * rowsPerPage - rows.length) : 0;

  const handleChangePage = (_event: MouseEvent<HTMLButtonElement> | null, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  useEffect(() => {
    if (isExtraLargeScreen) {
      setPage(0);
      setRowsPerPage(15);
    } else {
      setPage(0);
      setRowsPerPage(5);
    }
  }, [isExtraLargeScreen]);

  return (
    <TableContainer
      elevation={0}
      component={Paper}
      sx={{
        flex: 1,
        minHeight: 0,
        overflow: "auto",
        borderRadius: "8px",
        border: "1px solid #EBEBF7",
      }}
    >
      <Table sx={{ minWidth: 300, width: "100%" }} aria-label="Faults table" stickyHeader>
        <TableHead>
          <TableRow
            sx={{
              "& .MuiTableCell-root": {
                borderRight: "1px solid #EBEBF7",
                backgroundColor: "#FAFAFF",
                borderColor: "#EBEBF7",
                "&:last-of-type": {
                  borderRight: "none",
                },
              },
            }}
          >
            <StyledHeaderTableCell>
              <FormattedMessage id={`${headersId}.growthRate`} />
              <SortButton
                width={12}
                height={12}
                direction={sortOrder}
                onClick={handleSort(TrainDefectSortColumn.Rate)}
                isActive={sortColumn === TrainDefectSortColumn.Rate}
              />
            </StyledHeaderTableCell>
            <StyledHeaderTableCell>
              <FormattedMessage id={`${headersId}.position`} />
              <SortButton
                width={12}
                height={12}
                direction={sortOrder}
                onClick={handleSort(TrainDefectSortColumn.Position)}
                isActive={sortColumn === TrainDefectSortColumn.Position}
              />
            </StyledHeaderTableCell>
            <StyledHeaderTableCell>
              <FormattedMessage id={`${headersId}.date`} />
              <SortButton
                width={12}
                height={12}
                direction={sortOrder}
                onClick={handleSort(TrainDefectSortColumn.Date)}
                isActive={sortColumn === TrainDefectSortColumn.Date}
              />
            </StyledHeaderTableCell>
            <StyledHeaderTableCell>
              <FormattedMessage id={`${headersId}.name`} />
              <SortButton
                width={12}
                height={12}
                direction={sortOrder}
                onClick={handleSort(TrainDefectSortColumn.DefectName)}
                isActive={sortColumn === TrainDefectSortColumn.DefectName}
              />
            </StyledHeaderTableCell>
            <StyledHeaderTableCell>
              <FormattedMessage id={`${headersId}.status`} />
              <SortButton
                width={12}
                height={12}
                direction={sortOrder}
                onClick={handleSort(TrainDefectSortColumn.DefectStatus)}
                isActive={sortColumn === TrainDefectSortColumn.DefectStatus}
              />
            </StyledHeaderTableCell>
            <StyledHeaderTableCell>
              <FormattedMessage id={`${headersId}.source`} />
              <SortButton
                width={12}
                height={12}
                direction={sortOrder}
                onClick={handleSort(TrainDefectSortColumn.CreationType)}
                isActive={sortColumn === TrainDefectSortColumn.CreationType}
              />
            </StyledHeaderTableCell>
            <StyledHeaderTableCell>
              <FormattedMessage id={`${headersId}.report`} />
            </StyledHeaderTableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {(rowsPerPage > 0 ? rows.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage) : rows).map(row => (
            <TableRow
              key={v4()}
              sx={{
                "& .MuiTableCell-root": {
                  borderColor: "#EBEBF7",
                  borderRight: "1px solid #EBEBF7",
                  "&:last-of-type": {
                    borderRight: "none",
                  },
                },
              }}
            >
              <StyledTableCell sx={{ minWidth: 40 }}>
                <Box sx={{ display: "flex", justifyContent: "center" }}>
                  <SemaforIcon status={row.rate} width={18} height={18} labeled />
                </Box>
              </StyledTableCell>
              <StyledTableCell sx={{ minWidth: 80 }}>{row.position}</StyledTableCell>
              <StyledTableCell sx={{ minWidth: 80 }}>{row.date}</StyledTableCell>
              <StyledTableCell sx={{ minWidth: 80 }}>{row.name}</StyledTableCell>
              <StyledTableCell sx={{ minWidth: 80 }}>{row.status}</StyledTableCell>
              <StyledTableCell sx={{ minWidth: 80 }}>{row.source}</StyledTableCell>
              <StyledTableCell sx={{ minWidth: 40 }}>
                {row.idReport ? (
                  <ReportDownloadButton idReport={row.idReport} />
                ) : (
                  <FormattedMessage id={`${defectsId}.noReports`} />
                )}
              </StyledTableCell>
            </TableRow>
          ))}
          {emptyRows > 0 && (
            <TableRow sx={{ height: 53 * emptyRows }}>
              <TableCell colSpan={6} />
            </TableRow>
          )}
        </TableBody>
        {rows.length > rowsPerPage ? (
          <TableFooter>
            <TableRow
              sx={{
                "& .MuiToolbar-root.MuiTablePagination-toolbar": { minHeight: 30, fontSize: 12 },
                "& .MuiToolbar-root.MuiTablePagination-toolbar p": { fontSize: 12, m: 0 },
                "& .MuiToolbar-root .MuiButtonBase-root": { padding: 0.5 },
              }}
            >
              <StyledTablePagination
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                page={page}
                rowsLength={rows.length}
                rowsPerPage={rowsPerPage}
                colSpan={6}
              />
            </TableRow>
          </TableFooter>
        ) : null}
      </Table>
    </TableContainer>
  );
};
